import { useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, CircleMarker, Circle, Polyline, Tooltip, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Radio, Plane, LinkIcon, MapPin, LocateFixed } from 'lucide-react'
import { StreamKachel } from '../components/StreamKachel'
import { PilotMarkierungenLayer, type PilotMarkierung } from '../components/PilotMarkierungen'
import { KoordinatenBlock, KartenAppLink, markierungPunkte, flaechenDerGeometrie, geodaesie } from '@pilot-hub/ui/components/karte/index'

// Öffentliche Seite für Externe (Freigabelink /extern/<token>): Livebilder und Drohnenpositionen eines Einsatzes,
// ohne Login, ohne Bedienelemente, ohne Personendaten. Daten nur mit gültigem Token (fuehrung-api /extern/<token>/lage).

interface ExternDrohne {
  id: number
  kennung: string
  in_der_luft: boolean
  lat: number | null
  lon: number | null
  hoehe_m: number | null
  kurs: number | null
  flugdauer_s: number
  alter_s: number
  video_url: string | null
  weg: [number, number][]
}
interface ExternMarkierung {
  id: string
  name: string | null
  typ: number
  erstellt_am: string
  farbe: string | null
  geometrie: { type?: string; coordinates?: any } | null
}
interface ExternLage {
  markierungen?: ExternMarkierung[]
  einsatz: { nr: number; typ: string | null; lat: number | null; lon: number | null; radius_m: number | null }
  drohnen: ExternDrohne[]
  stand: string
}

const FARBEN = ['#d9282f', '#38bdf8', '#facc15', '#4ade80', '#c084fc', '#fb923c']
const TYP: Record<string, string> = { EINSATZ: 'Einsatz', UEBUNG: 'Übung' }

const dauer = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`
}

function Fokus({ ziel, n }: { ziel: [number, number]; n: number }) {
  const map = useMap()
  useEffect(() => { map.setView(ziel, Math.max(map.getZoom(), 18)) }, [n])   // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

function Anpassen({ punkte }: { punkte: [number, number][] }) {
  const map = useMap()
  const schluessel = punkte.length ? String(punkte.length) : ''
  const letzter = useRef('')
  useEffect(() => {
    // Nur bei neuer Drohne bzw. erstem Fix einpassen, damit die Ansicht beim Verschieben durch Nutzer ruhig bleibt
    if (!punkte.length || letzter.current === schluessel) return
    letzter.current = schluessel
    if (punkte.length === 1) map.setView(punkte[0], Math.max(map.getZoom(), 16))
    else map.fitBounds(L.latLngBounds(punkte), { padding: [50, 50], maxZoom: 17 })
  }, [schluessel, punkte, map])
  return null
}

/**
 * Wandmodus: großer Bildschirm (TV / Videowand, ab 1600 x 800 px): die Seite füllt genau den Bildschirm ohne Scrollen,
 * Livebilder als Raster links, Karte rechts, große Schrift. Kleinere Bildschirme behalten das bisherige Layout.
 */
function useWand() {
  const lesen = () => ({
    wand: typeof window !== 'undefined' && window.matchMedia('(min-width: 1600px) and (min-height: 800px)').matches,
    b: window.innerWidth, h: window.innerHeight,
  })
  const [m, setM] = useState(lesen)
  useEffect(() => {
    const f = () => setM(lesen())
    window.addEventListener('resize', f)
    return () => window.removeEventListener('resize', f)
  }, [])
  return m
}

/** Spalten/Reihen so wählen, dass die Kacheln möglichst nah am Seitenverhältnis 16:9 bleiben */
function rasterFuer(n: number, b: number, h: number) {
  let best = { c: 1, r: Math.max(1, n) }, score = Infinity
  for (let c = 1; c <= Math.max(1, n); c++) {
    const r = Math.ceil(n / c)
    const s = Math.abs(Math.log(((b / c) / (h / r)) / (16 / 9))) + (c * r - n) * 0.15
    if (s < score) { score = s; best = { c, r } }
  }
  return best
}

export function ExternPage({ token }: { token: string }) {
  const [lage, setLage] = useState<ExternLage | null>(null)
  const [ungueltig, setUngueltig] = useState<null | 'abgelaufen' | 'gesperrt' | 'unbekannt'>(null)
  const [unterbrochen, setUnterbrochen] = useState(false)
  const erst = useRef(true)
  const { wand, b, h } = useWand()
  const [vollbild, setVollbild] = useState(false)
  const [fokus, setFokus] = useState<{ pkt: [number, number]; n: number } | null>(null)
  const [ich, setIch] = useState<[number, number] | null>(null)
  const [ichGenau, setIchGenau] = useState<number | null>(null)
  const [ichFehler, setIchFehler] = useState<string | null>(null)
  const zentrierenBeiFix = useRef(false)
  const [offen, setOffen] = useState<string | null>(null)
  const kartenRef = useRef<HTMLElement | null>(null)

  // Eigener Standort des Betrachters (nur auf Wunsch; bleibt im Browser, wird nirgends hin gesendet)
  const geoId = useRef<number | null>(null)
  // Standort braucht eine sichere Verbindung (HTTPS) und die Freigabe des Browsers
  const standortMoeglich = typeof navigator !== 'undefined' && !!navigator.geolocation && (typeof window === 'undefined' || window.isSecureContext !== false)
  const standortAn = (zentrieren = false) => {
    if (!standortMoeglich) { setIchFehler('Der eigene Standort ist auf diesem Gerät bzw. bei dieser (nicht gesicherten) Verbindung nicht verfügbar.'); return }
    if (geoId.current != null) {
      if (zentrieren && ich) setFokus(f => ({ pkt: ich, n: (f?.n ?? 0) + 1 }))
      else if (zentrieren) zentrierenBeiFix.current = true
      return
    }
    setIchFehler(null)
    zentrierenBeiFix.current = zentrieren
    geoId.current = navigator.geolocation.watchPosition(
      p => {
        const pkt: [number, number] = [p.coords.latitude, p.coords.longitude]
        setIch(pkt); setIchGenau(p.coords.accuracy ?? null)
        if (zentrierenBeiFix.current) { zentrierenBeiFix.current = false; setFokus(f => ({ pkt, n: (f?.n ?? 0) + 1 })) }
      },
      e => {
        setIchFehler(e.code === 1 ? 'Standortfreigabe abgelehnt – bitte im Browser erlauben, um Ihren Standort auf der Karte zu sehen.' : 'Standort derzeit nicht verfügbar.')
        if (geoId.current != null) navigator.geolocation.clearWatch(geoId.current)
        geoId.current = null; setIch(null); setIchGenau(null)
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 })
  }
  useEffect(() => () => { if (geoId.current != null) navigator.geolocation.clearWatch(geoId.current) }, [])

  // Vollbild (F oder Knopf) und Bildschirm wachhalten, solange die Seite offen ist (Videowand/TV)
  useEffect(() => {
    const f = () => setVollbild(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', f)
    const taste = (e: KeyboardEvent) => { if (e.key.toLowerCase() === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) vollbildUmschalten() }
    window.addEventListener('keydown', taste)
    let sperre: { release: () => Promise<void> } | null = null
    const halten = async () => {
      try { if (document.visibilityState === 'visible') sperre = await (navigator as any).wakeLock?.request('screen') ?? null } catch { /* nicht unterstützt */ }
    }
    void halten()
    document.addEventListener('visibilitychange', halten)
    return () => {
      document.removeEventListener('fullscreenchange', f); window.removeEventListener('keydown', taste)
      document.removeEventListener('visibilitychange', halten); void sperre?.release().catch(() => {})
    }
  }, [])
  const vollbildUmschalten = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {})
    else void document.documentElement.requestFullscreen?.().catch(() => {})
  }

  useEffect(() => {
    document.title = 'Live-Lage – ASB Drohnenstaffel'
    const meta = document.createElement('meta')
    meta.name = 'robots'; meta.content = 'noindex'
    document.head.appendChild(meta)
    let aktiv = true
    let t: number | undefined
    const holen = async () => {
      try {
        const r = await fetch(`/api/v1/extern/${encodeURIComponent(token)}/lage${erst.current ? '?erst=1' : ''}`, { cache: 'no-store' })
        if (!aktiv) return
        if (r.status === 404) { setUngueltig('unbekannt'); return }
        if (r.status === 410) { setUngueltig((await r.json()).detail === 'gesperrt' ? 'gesperrt' : 'abgelaufen'); return }
        if (!r.ok) throw new Error(String(r.status))
        erst.current = false
        setLage(await r.json()); setUnterbrochen(false)
      } catch {
        if (aktiv) setUnterbrochen(true)
      }
      if (aktiv) t = window.setTimeout(holen, document.visibilityState === 'visible' ? 4000 : 15000)
    }
    holen()
    return () => { aktiv = false; if (t) window.clearTimeout(t); meta.remove() }
  }, [token])

  const punkte = useMemo(() => {
    const p: [number, number][] = []
    lage?.drohnen.forEach(d => { if (d.lat && d.lon) p.push([d.lat, d.lon]) })
    return p
  }, [lage])

  const marken = useMemo(() => (lage?.markierungen ?? []).map(m => ({
    roh: m,
    ang: { id: m.id, name: m.name, typ: m.typ, inhalt: { properties: { color: m.farbe ?? undefined }, geometry: m.geometrie ?? undefined }, ersteller: null, erstellt_am: m.erstellt_am } as PilotMarkierung,
    pkt: markierungPunkte(m.geometrie)[0]?.pkt ?? null,
  })), [lage])

  if (ungueltig) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-bg p-6">
        <div className="max-w-md rounded-2xl border border-dark-border bg-dark-bg-secondary p-8 text-center">
          <LinkIcon className="mx-auto mb-3 h-10 w-10 text-dark-text-muted" />
          <h1 className="mb-2 text-xl font-semibold text-dark-text">Link nicht mehr gültig</h1>
          <p className="text-sm text-dark-text-muted">
            {ungueltig === 'abgelaufen' && 'Der Einsatz ist beendet – die Freigabe endet automatisch mit dem Einsatzende.'}
            {ungueltig === 'gesperrt' && 'Dieser Link wurde von der Einsatzleitung gesperrt oder durch einen neuen Link ersetzt.'}
            {ungueltig === 'unbekannt' && 'Dieser Link ist unbekannt oder wurde nicht vollständig übernommen.'}
          </p>
          <p className="mt-3 text-sm text-dark-text-muted">Bitte bei der Einsatzleitung einen aktuellen Link bzw. QR-Code anfordern.</p>
        </div>
      </div>
    )
  }
  if (!lage) {
    return <div className="flex min-h-screen items-center justify-center bg-dark-bg">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
  }

  const mitBild = lage.drohnen.filter(d => d.video_url)
  const zentrum: [number, number] = lage.einsatz.lat && lage.einsatz.lon ? [lage.einsatz.lat, lage.einsatz.lon] : [51.1657, 10.4515]

  const n = lage.drohnen.length
  const raster = rasterFuer(n, Math.round(b / 2) - 40, h - 130)

  const kachel = (d: ExternDrohne, i: number) => (
    <StreamKachel
      key={d.id}
      titel={d.kennung}
      untertitel={`${d.hoehe_m != null ? Math.round(d.hoehe_m) + ' m' : '– m'} · ${dauer(d.flugdauer_s)}${d.alter_s > 10 ? ' · keine aktuellen Daten' : ''}`}
      url={d.video_url}
      fuellen={wand}
      gross={wand}
      farbe={wand ? FARBEN[i % FARBEN.length] : undefined}
    />
  )

  return (
    <div className={`bg-dark-bg text-dark-text ${wand ? 'h-[100dvh] overflow-hidden' : 'min-h-screen'}`} data-wand={wand ? '1' : '0'}>
      <header className={`z-[1000] flex flex-wrap items-center justify-between gap-2 border-b border-dark-border bg-dark-bg-secondary ${wand ? 'h-20 px-8' : 'sticky top-0 px-4 py-3'}`}>
        <div className="flex items-center gap-3">
          <span className={`relative flex ${wand ? 'h-5 w-5' : 'h-3 w-3'}`}><span className={`absolute inline-flex h-full w-full rounded-full ${unterbrochen ? 'bg-amber-400' : 'animate-ping bg-red-500'} opacity-60`} /><span className={`relative inline-flex rounded-full ${wand ? 'h-5 w-5' : 'h-3 w-3'} ${unterbrochen ? 'bg-amber-400' : 'bg-red-500'}`} /></span>
          <span className={`font-semibold ${wand ? 'text-4xl' : ''}`}>Live – {TYP[lage.einsatz.typ ?? ''] ?? 'Einsatz'} Nr. {lage.einsatz.nr}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`text-dark-text-muted ${wand ? 'text-2xl' : 'text-xs'}`}>
            {unterbrochen ? 'Verbindung unterbrochen – versuche erneut …' : `Stand ${new Date(lage.stand).toLocaleTimeString('de-DE')}`}
            {!wand && <>{' · '}Freigabe endet mit dem Einsatzende</>}
          </div>
          {wand && !vollbild && (
            <button type="button" onClick={vollbildUmschalten} title="Vollbild (Taste F)" aria-label="Vollbild"
              className="rounded-lg border border-dark-border px-4 py-2 text-lg text-dark-text-muted hover:text-dark-text">Vollbild</button>
          )}
        </div>
      </header>

      <main className={wand ? 'grid h-[calc(100dvh-5rem)] grid-cols-2 gap-6 p-6' : 'grid gap-4 p-4 lg:grid-cols-2'}>
        <section className={wand ? 'min-h-0' : 'space-y-4'}>
          {!wand && marken.length > 0 && (
            <div className="rounded-xl border-2 border-red-500/70 bg-dark-bg-secondary p-3" data-testid="markierte-stellen">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-base font-semibold"><MapPin className="h-5 w-5 text-red-500" /> Markierte Stellen ({marken.length})</h2>
                {standortMoeglich && (
                  <button type="button" onClick={() => standortAn(true)} data-testid="standort-knopf"
                    className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-dark-border px-3 py-1.5 text-sm text-dark-text-muted hover:text-dark-text">
                    <LocateFixed className="h-4 w-4" /> {ich ? 'Zu meinem Standort' : 'Mein Standort'}
                  </button>
                )}
              </div>
              {(ichFehler || !standortMoeglich) && <p className="mb-2 text-xs text-amber-400" data-testid="standort-hinweis">{ichFehler ?? 'Der eigene Standort ist hier nicht verfügbar (nur über eine gesicherte Verbindung und mit Freigabe des Browsers).'}</p>}
              <ul className="space-y-2">
                {marken.map(({ roh, pkt }) => {
                  const d = ich && pkt ? geodaesie.strecke(ich, pkt) : null
                  const auf = offen === roh.id
                  return (
                    <li key={roh.id} className="rounded-lg border border-dark-border bg-dark-bg p-2.5">
                      <button type="button" className="block w-full text-left" data-testid="markierung-eintrag"
                        onClick={() => {
                          setOffen(auf ? null : roh.id)
                          if (pkt) { setFokus(f => ({ pkt, n: (f?.n ?? 0) + 1 })); kartenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }
                        }}>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium">{roh.name || 'Markierung'}</span>
                          <span className="shrink-0 text-xs text-dark-text-muted">{new Date(roh.erstellt_am).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
                        </div>
                        {pkt ? <div className="mt-0.5 font-mono text-xs text-dark-text-muted">{geodaesie.koordDezimal(pkt)}</div> : <div className="text-xs text-dark-text-muted">Keine Position</div>}
                        {roh.geometrie?.type === 'Polygon' && flaechenDerGeometrie(roh.geometrie) > 0 && <div className="mt-0.5 text-xs text-dark-text-muted" data-testid="markierung-flaeche-liste">Fläche: {geodaesie.textFlaecheStufen(flaechenDerGeometrie(roh.geometrie))}</div>}
                        {d && <div className="mt-0.5 text-xs text-sky-400" data-testid="markierung-entfernung">{geodaesie.textLaenge(d.m)} Richtung {geodaesie.himmelsrichtung(d.peilung)} ({Math.round(d.peilung)}°) von Ihnen</div>}
                      </button>
                      {auf && pkt && (
                        <div className="mt-2 rounded-md bg-white p-2 text-gray-900">
                          <KoordinatenBlock pkt={pkt} />
                          <KartenAppLink pkt={pkt} name={roh.name} />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {n === 0 ? (
            <div className={`rounded-xl border border-dark-border bg-dark-bg-secondary text-center ${wand ? 'flex h-full flex-col items-center justify-center p-10' : 'p-10'}`}>
              <Plane className={`mx-auto mb-2 text-dark-text-muted ${wand ? 'h-16 w-16' : 'h-8 w-8'}`} />
              <p className={`font-medium ${wand ? 'text-4xl' : ''}`}>Keine Drohne in der Luft</p>
              <p className={`mt-1 text-dark-text-muted ${wand ? 'text-2xl' : 'text-sm'}`}>Livebild und Position erscheinen hier automatisch, sobald eine Drohne dieses Einsatzes startet.</p>
            </div>
          ) : wand ? (
            <div className="grid h-full gap-4" data-testid="extern-raster"
              style={{ gridTemplateColumns: `repeat(${raster.c}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${raster.r}, minmax(0, 1fr))` }}>
              {lage.drohnen.map(kachel)}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {lage.drohnen.map((d, i) => (
                <div key={d.id}>
                  {kachel(d, i)}
                  <div className="mt-1 flex items-center gap-1.5 px-1 text-xs text-dark-text-muted">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: FARBEN[i % FARBEN.length] }} />
                    {d.video_url ? 'Livebild läuft' : 'Kein Livebild – Drohne sendet derzeit kein Bild'}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!wand && n > 0 && mitBild.length === 0 && (
            <p className="flex items-center gap-1.5 text-xs text-dark-text-muted"><Radio className="h-3.5 w-3.5" /> Es läuft derzeit kein Livebild; die Positionen sind trotzdem aktuell.</p>
          )}
        </section>

        <section ref={kartenRef} className={wand ? 'h-full overflow-hidden rounded-xl border border-dark-border' : 'h-[60vh] min-h-[320px] overflow-hidden rounded-xl border border-dark-border lg:sticky lg:top-20 lg:h-[calc(100vh-6.5rem)]'}>
          <div className="relative h-full w-full">
          {standortMoeglich && !wand && (
            <button type="button" onClick={() => standortAn(true)} data-testid="standort-karte-knopf" aria-label="Mein Standort"
              className="absolute bottom-6 left-3 z-[1000] flex h-11 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 shadow">
              <LocateFixed className="h-4 w-4 text-blue-600" /> Mein Standort
            </button>
          )}
          {!wand && ichFehler && marken.length === 0 && (
            <p className="absolute bottom-20 left-3 right-3 z-[1000] rounded-lg bg-white/95 px-3 py-2 text-xs text-amber-700 shadow" data-testid="standort-hinweis-karte">{ichFehler}</p>
          )}
          <MapContainer center={zentrum} zoom={15} className="h-full w-full" attributionControl>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" maxZoom={19} />
            {lage.einsatz.lat && lage.einsatz.lon && (
              <>
                <CircleMarker center={[lage.einsatz.lat, lage.einsatz.lon]} radius={7} pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#C6291A', fillOpacity: 1 }}>
                  <Tooltip>Einsatzort</Tooltip>
                </CircleMarker>
                {lage.einsatz.radius_m ? <Circle center={[lage.einsatz.lat, lage.einsatz.lon]} radius={lage.einsatz.radius_m} pathOptions={{ color: '#C6291A', weight: 2, fill: false, dashArray: '6 6' }} /> : null}
              </>
            )}
            {lage.drohnen.map((d, i) => {
              const farbe = FARBEN[i % FARBEN.length]
              return (
                <span key={d.id}>
                  {d.weg.length > 1 && <Polyline positions={d.weg} pathOptions={{ color: farbe, weight: 3, opacity: 0.85 }} />}
                  {d.lat && d.lon && (
                    <CircleMarker center={[d.lat, d.lon]} radius={9} pathOptions={{ color: '#ffffff', weight: 3, fillColor: farbe, fillOpacity: d.alter_s > 10 ? 0.5 : 1 }}>
                      <Tooltip permanent direction="top" offset={[0, -10]}>
                        <b>{d.kennung}</b> · {d.hoehe_m != null ? Math.round(d.hoehe_m) : '–'} m
                      </Tooltip>
                    </CircleMarker>
                  )}
                </span>
              )
            })}
            <PilotMarkierungenLayer markierungen={marken.map(m => m.ang)} gross />
            {ich && ichGenau != null && ichGenau > 0 && <Circle center={ich} radius={ichGenau} pathOptions={{ color: '#2563eb', weight: 1, fillColor: '#2563eb', fillOpacity: 0.12 }} interactive={false} />}
            {ich && <CircleMarker center={ich} radius={8} pathOptions={{ color: '#fff', weight: 3, fillColor: '#2563eb', fillOpacity: 1 }}><Tooltip>Mein Standort{ichGenau != null ? ` (±${Math.round(ichGenau)} m)` : ''}</Tooltip></CircleMarker>}
            <Anpassen punkte={punkte} />
            {fokus && <Fokus ziel={fokus.pkt} n={fokus.n} />}
          </MapContainer>
          </div>
        </section>
      </main>
    </div>
  )
}
